import React, { useCallback, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box,
    Switch,
    FormControlLabel,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    IconButton,
    Tooltip,
    LinearProgress,
    Chip,
    Link,
    useMediaQuery
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { useDropzone } from "react-dropzone";
import { useAtom } from "jotai";
import { useTheme } from "@mui/material/styles";

import {
    companyAtom,
    mbxUserAtom,
    openImportClientsModalAtom
} from "../atoms/atoms";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import { postApi } from "../modules/AmplifyServices";
import {
    parseClientsCsv,
    buildClientPayload,
    downloadCsvTemplate,
    downloadErrorCsv,
    geocodeAddress,
    hasAnyAddressField,
    buildAddressOneLine,
    REQUIRED_ADDRESS_FIELDS
} from "../utils/importClients";

const CONCURRENCY = 15;
const MAX_IMPORT_ROWS = 500;
const STEPS = { UPLOAD: "upload", PREVIEW: "preview", RESULTS: "results" };

const runPool = async (items, concurrency, worker) => {
    let nextIndex = 0;
    const runners = Array.from(
        { length: Math.min(concurrency, items.length) },
        async () => {
            while (true) {
                const i = nextIndex++;
                if (i >= items.length) return;
                await worker(items[i], i);
            }
        }
    );
    await Promise.all(runners);
};

const ImportClientsModal = ({ onComplete }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const [openImport, setOpenImport] = useAtom(openImportClientsModalAtom);
    const [company] = useAtom(companyAtom);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [, setSnackbar] = useAtom(userPageSnackbarAtom);

    const [step, setStep] = useState(STEPS.UPLOAD);
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
    const [parseError, setParseError] = useState("");
    const [isImporting, setIsImporting] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [geocodeProgress, setGeocodeProgress] = useState({
        done: 0,
        total: 0
    });

    const resetState = () => {
        setStep(STEPS.UPLOAD);
        setFileName("");
        setRows([]);
        setParseError("");
        setSendWelcomeEmail(true);
        setIsImporting(false);
        setCompletedCount(0);
        setIsGeocoding(false);
        setGeocodeProgress({ done: 0, total: 0 });
    };

    const handleClose = () => {
        if (isImporting || isGeocoding) return;
        setOpenImport(false);
        resetState();
    };

    const runGeocoding = async (parsedRows) => {
        // Only geocode rows that have all required address fields.
        const toGeocode = parsedRows.filter(
            (r) =>
                hasAnyAddressField(r) &&
                REQUIRED_ADDRESS_FIELDS.every((f) => r[f])
        );
        if (!toGeocode.length) return;

        setIsGeocoding(true);
        setGeocodeProgress({ done: 0, total: toGeocode.length });

        // Mark these rows as geocoding so the preview shows a live status.
        setRows((prev) =>
            prev.map((r) =>
                toGeocode.some((g) => g.id === r.id)
                    ? { ...r, addressStatus: "geocoding" }
                    : r
            )
        );

        await runPool(toGeocode, CONCURRENCY, async (row) => {
            const result = await geocodeAddress(row);
            setRows((prev) =>
                prev.map((r) => {
                    if (r.id !== row.id) return r;
                    if (result.success) {
                        return {
                            ...r,
                            addressStatus: "geocoded",
                            geocode: result
                        };
                    }
                    // Geocode failed → mark row invalid so it can't import.
                    const errMsg = `Couldn't locate address${
                        result.error ? ` (${result.error})` : ""
                    }`;
                    return {
                        ...r,
                        addressStatus: "geocode_failed",
                        geocode: result,
                        errors: [...r.errors, errMsg],
                        status: "invalid"
                    };
                })
            );
            setGeocodeProgress((p) => ({ ...p, done: p.done + 1 }));
        });

        setIsGeocoding(false);
    };

    const handleFile = useCallback(async (file) => {
        setParseError("");
        try {
            const text = await file.text();
            const { rows: parsed } = parseClientsCsv(text);
            if (!parsed.length) {
                setParseError("No rows found in the CSV.");
                return;
            }
            if (parsed.length > MAX_IMPORT_ROWS) {
                setParseError(
                    `This file has ${parsed.length} rows. Please split it into files of ${MAX_IMPORT_ROWS} rows or fewer and import them separately.`
                );
                return;
            }
            // Seed addressStatus based on whether each row has any address data.
            const seeded = parsed.map((r) => ({
                ...r,
                addressStatus: hasAnyAddressField(r) ? "pending" : "none"
            }));
            setRows(seeded);
            setFileName(file.name);
            setStep(STEPS.PREVIEW);
            runGeocoding(seeded);
        } catch (e) {
            console.log("CSV parse error", e);
            setParseError("Could not read the CSV file.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDrop = useCallback(
        (accepted) => {
            if (accepted?.[0]) handleFile(accepted[0]);
        },
        [handleFile]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "text/csv": [".csv"] },
        maxFiles: 1,
        multiple: false
    });

    const counts = useMemo(() => {
        const valid = rows.filter((r) => r.errors.length === 0).length;
        return { valid, invalid: rows.length - valid, total: rows.length };
    }, [rows]);

    const successCount = useMemo(
        () => rows.filter((r) => r.status === "success").length,
        [rows]
    );
    const failedRows = useMemo(
        () =>
            rows.filter(
                (r) => r.status === "failed" || r.status === "invalid"
            ),
        [rows]
    );

    const handleRemoveRow = (id) =>
        setRows((prev) => prev.filter((r) => r.id !== id));

    const handleStartImport = async () => {
        const validRows = rows.filter((r) => r.errors.length === 0);
        if (!validRows.length) return;
        setIsImporting(true);
        setStep(STEPS.RESULTS);
        setCompletedCount(0);

        setRows((prev) =>
            prev.map((r) =>
                r.errors.length === 0 ? { ...r, status: "importing" } : r
            )
        );

        await runPool(validRows, CONCURRENCY, async (row) => {
            const payload = buildClientPayload({
                row,
                company,
                mbxUser,
                sendWelcomeEmail
            });
            let next = { status: "failed", apiMessage: "Unknown error" };
            try {
                const res = await postApi("userManagement", "/client", {
                    body: payload
                });
                if (
                    res?.newUserRecord &&
                    res.status >= 200 &&
                    res.status < 300
                ) {
                    next = { status: "success", apiMessage: "" };
                } else {
                    const msg =
                        res?.status === 409
                            ? "Email already exists"
                            : res?.message ||
                              `Failed${res?.status ? ` (${res.status})` : ""}`;
                    next = { status: "failed", apiMessage: msg };
                }
            } catch (e) {
                next = {
                    status: "failed",
                    apiMessage: e?.message || "Network error"
                };
            }
            setRows((prev) =>
                prev.map((r) => (r.id === row.id ? { ...r, ...next } : r))
            );
            setCompletedCount((c) => c + 1);
        });

        setIsImporting(false);
        if (onComplete) onComplete();
    };

    const handleFinish = () => {
        const created = rows.filter((r) => r.status === "success").length;
        if (created > 0) {
            setSnackbar({
                open: true,
                message: `${created} client${
                    created === 1 ? "" : "s"
                } imported successfully`,
                severity: "success"
            });
        }
        handleClose();
    };

    const validRowCount = counts.valid;

    const renderUploadStep = () => (
        <DialogContent sx={{ minHeight: "320px" }}>
            <Box
                {...getRootProps()}
                sx={{
                    border: "2px dashed",
                    borderColor: isDragActive ? "primary.main" : "#DEDEDE",
                    borderRadius: 1,
                    padding: "40px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color .2s ease-in-out",
                    "&:hover": { borderColor: "primary.main" }
                }}
            >
                <input {...getInputProps()} />
                <CloudUploadIcon
                    sx={{ fontSize: 48, color: "#6B6B6C", mb: 1 }}
                />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {isDragActive
                        ? "Drop the CSV here"
                        : "Drag & drop a CSV, or click to select"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B6B6C", mt: 1 }}>
                    Required: firstName, lastName, email, phone
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B6B6C" }}>
                    Optional: notes, street, city, state, postalCode, country
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: "#6B6B6C", display: "block" }}
                >
                    Up to {MAX_IMPORT_ROWS} clients per file
                </Typography>
            </Box>

            {parseError && (
                <Typography sx={{ color: "error.main", mt: 2 }}>
                    {parseError}
                </Typography>
            )}

            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 3 }}
            >
                <Grid item>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={sendWelcomeEmail}
                                onChange={(e) =>
                                    setSendWelcomeEmail(e.target.checked)
                                }
                            />
                        }
                        label="Send welcome email to all imported clients"
                    />
                </Grid>
                <Grid item>
                    <Link
                        component="button"
                        type="button"
                        onClick={downloadCsvTemplate}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px"
                        }}
                    >
                        <DescriptionIcon fontSize="small" />
                        Download template
                    </Link>
                </Grid>
            </Grid>
        </DialogContent>
    );

    const renderAddressCell = (row) => {
        if (row.addressStatus === "none" || !hasAnyAddressField(row)) {
            return (
                <Typography
                    variant="body2"
                    sx={{ color: "#9B9B9B", fontStyle: "italic" }}
                >
                    —
                </Typography>
            );
        }
        if (row.addressStatus === "geocoding" || row.addressStatus === "pending") {
            return (
                <Chip
                    label="Locating…"
                    size="small"
                    variant="outlined"
                />
            );
        }
        if (row.addressStatus === "geocoded") {
            return (
                <Tooltip title={row.geocode?.addressOneLine || ""}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Address ready"
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }
        if (row.addressStatus === "geocode_failed") {
            return (
                <Tooltip
                    title={`${buildAddressOneLine(row)} — ${
                        row.geocode?.error || "Could not locate"
                    }`}
                >
                    <Chip
                        icon={<ErrorOutlineIcon />}
                        label="Not found"
                        size="small"
                        color="error"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }
        return null;
    };

    const renderPreviewStep = () => {
        const geocodePct = geocodeProgress.total
            ? (geocodeProgress.done / geocodeProgress.total) * 100
            : 0;
        return (
            <DialogContent sx={{ minHeight: "420px" }}>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <Grid item>
                        <Typography variant="body2" sx={{ color: "#6B6B6C" }}>
                            {fileName}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Chip
                            label={`${counts.valid} valid`}
                            color="success"
                            size="small"
                            sx={{ mr: 1 }}
                        />
                        {counts.invalid > 0 && (
                            <Chip
                                label={`${counts.invalid} invalid`}
                                color="error"
                                size="small"
                            />
                        )}
                    </Grid>
                </Grid>
                {isGeocoding && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Validating addresses… {geocodeProgress.done} of{" "}
                            {geocodeProgress.total}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={geocodePct}
                        />
                    </Box>
                )}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }}>#</TableCell>
                                <TableCell>First name</TableCell>
                                <TableCell>Last name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Issues</TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ width: 50 }}
                                />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const isInvalid = row.errors.length > 0;
                                return (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            backgroundColor: isInvalid
                                                ? "rgba(244, 67, 54, 0.06)"
                                                : "inherit"
                                        }}
                                    >
                                        <TableCell>{row.rowNumber}</TableCell>
                                        <TableCell>{row.firstName}</TableCell>
                                        <TableCell>{row.lastName}</TableCell>
                                        <TableCell
                                            sx={{
                                                minWidth: 260,
                                                overflowWrap: "anywhere"
                                            }}
                                        >
                                            {row.email}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {row.phone}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 220,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            <Tooltip title={row.notes || ""}>
                                                <span>{row.notes}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {renderAddressCell(row)}
                                        </TableCell>
                                        <TableCell>
                                            {isInvalid ? (
                                                <Tooltip
                                                    title={row.errors.join(
                                                        "; "
                                                    )}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "error.main"
                                                        }}
                                                    >
                                                        {row.errors[0]}
                                                        {row.errors.length > 1
                                                            ? ` (+${
                                                                  row.errors
                                                                      .length -
                                                                  1
                                                              })`
                                                            : ""}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Chip
                                                    label="Ready"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleRemoveRow(row.id)
                                                }
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Switch
                            checked={sendWelcomeEmail}
                            onChange={(e) =>
                                setSendWelcomeEmail(e.target.checked)
                            }
                        />
                    }
                    label="Send welcome email to all imported clients"
                />
            </DialogContent>
        );
    };

    const renderResultsStep = () => {
        const totalToImport = rows.filter(
            (r) => r.status !== "invalid"
        ).length;
        const progressPct = totalToImport
            ? (completedCount / totalToImport) * 100
            : 0;
        return (
            <DialogContent sx={{ minHeight: "420px" }}>
                <Box sx={{ mb: 3 }}>
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Typography variant="body2">
                            {isImporting
                                ? `Importing… ${completedCount} of ${totalToImport}`
                                : `Done — ${successCount} succeeded, ${
                                      totalToImport - successCount
                                  } failed`}
                        </Typography>
                        {!isImporting && failedRows.length > 0 && (
                            <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => downloadErrorCsv(failedRows)}
                            >
                                Download errors
                            </Button>
                        )}
                    </Grid>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                    />
                </Box>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }}>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.rowNumber}</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {row.firstName} {row.lastName}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            minWidth: 260,
                                            overflowWrap: "anywhere"
                                        }}
                                    >
                                        {row.email}
                                    </TableCell>
                                    <TableCell>
                                        {row.status === "success" && (
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label="Created"
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        )}
                                        {row.status === "failed" && (
                                            <Tooltip
                                                title={row.apiMessage || ""}
                                            >
                                                <Chip
                                                    icon={
                                                        <ErrorOutlineIcon />
                                                    }
                                                    label={
                                                        row.apiMessage ||
                                                        "Failed"
                                                    }
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        )}
                                        {row.status === "importing" && (
                                            <Chip
                                                label="Importing…"
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                        {row.status === "invalid" && (
                                            <Tooltip
                                                title={row.errors.join("; ")}
                                            >
                                                <Chip
                                                    label="Skipped"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        );
    };

    return (
        <Dialog
            open={openImport}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ paddingBottom: "16px" }}>
                Import clients
            </DialogTitle>

            {step === STEPS.UPLOAD && renderUploadStep()}
            {step === STEPS.PREVIEW && renderPreviewStep()}
            {step === STEPS.RESULTS && renderResultsStep()}

            <DialogActions sx={{ padding: "16px 24px" }}>
                {step === STEPS.UPLOAD && (
                    <Button
                        onClick={handleClose}
                        variant="cancelButton"
                    >
                        Cancel
                    </Button>
                )}
                {step === STEPS.PREVIEW && (
                    <>
                        {isDesktop && (
                            <Button
                                onClick={resetState}
                                variant="cancelButton"
                                disabled={isGeocoding}
                            >
                                Back
                            </Button>
                        )}
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            loading={isGeocoding}
                            disabled={validRowCount === 0 || isGeocoding}
                            onClick={handleStartImport}
                        >
                            {isGeocoding
                                ? "Validating addresses…"
                                : `Import ${validRowCount} client${
                                      validRowCount === 1 ? "" : "s"
                                  }`}
                        </LoadingButton>
                    </>
                )}
                {step === STEPS.RESULTS && (
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={isImporting}
                        onClick={handleFinish}
                    >
                        Done
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ImportClientsModal;
